import React, { ReactElement } from 'react';
import {
  CreateOrderActions,
  CreateOrderData,
  CreateOrderRequestBody,
  OnApproveData,
} from '@paypal/paypal-js';
import {
  PayPalButtons,
  PayPalScriptProvider,
  usePayPalScriptReducer,
} from '@paypal/react-paypal-js';
import { Button } from '@appsumo/dorado-react';

import { useCheckoutState, useCheckoutAPI } from '~/hooks/checkout';
import { CartProps } from '~/types/cart';
import { STEP } from '~/constants/checkout';
import { fetchJson } from '~/lib/fetch';

import { Loader } from '~/components/ui';

const DEFAULT_ERROR = 'Error checking out with PayPal. Please try again.';

const createOrder = (
  _data: CreateOrderData,
  actions: CreateOrderActions,
  cart: CartProps,
): Promise<string> => {
  function _constructOrderObj(): CreateOrderRequestBody {
    const order: CreateOrderRequestBody = {
      intent: 'CAPTURE',
      // todo lower priority: opt for experience_context instead of application_context
      application_context: {
        brand_name: 'AppSumo',
        shipping_preference: 'NO_SHIPPING',
        user_action: 'PAY_NOW',
      },
      purchase_units: [
        {
          soft_descriptor: 'Purchase',
          amount: {
            currency_code: 'USD',
            value: String(cart.total),
            breakdown: {
              discount: {
                currency_code: 'USD',
                value: String(
                  (
                    cart.coupon_discount +
                    cart.plan_discount +
                    cart.credit
                  ).toFixed(2),
                ),
              },
              item_total: {
                currency_code: 'USD',
                value: String(cart.subtotal),
              },
              tax_total: {
                currency_code: 'USD',
                value: String(cart.tax),
              },
            },
          },
          items: cart.cart_items.map((item: any) => ({
            name: item.deal.public_name,
            quantity: item.quantity,
            category: 'DIGITAL_GOODS',
            sku: item.deal_plan.id,
            unit_amount: {
              currency_code: 'USD',
              value: item.price,
            },
          })),
        },
      ],
    };

    return order;
  }

  return actions.order.create(_constructOrderObj());
};

const PaypalButtonWrapper = ({
  isDisabled,
}: {
  isDisabled: boolean;
}): ReactElement => {
  const { cart } = useCheckoutState();
  const { setStepErrorMessage } = useCheckoutAPI();
  const [{ isPending }] = usePayPalScriptReducer();

  const onApprove = (data: OnApproveData): Promise<any> => {
    const formData = new FormData();
    formData.append('paypal_order_id', data?.orderID); // BE handles missing/invalid orderID
    formData.append('paypal', 'True');

    return fetchJson('/checkout/', {
      method: 'POST',
      include: 'credentials',
      body: formData,
    })
      .then((res) => {
        // todo: useRouter() once success page has been ported to next
        window.location.href = `${
          process.env.NEXT_PUBLIC_DJANGO_PUBLIC_URL ?? ''
        }${res.success_url}`;
      })
      .catch(async (e: any) => {
        const { error: errMsg } = await e.response.json();
        setStepErrorMessage(STEP.PAYMENT, errMsg || DEFAULT_ERROR);
        console.error(e);
      });
  };

  const onError = (err: Record<string, unknown>): void => {
    // error clears on update to payment section, so shouldn't need to clear error here
    setStepErrorMessage(STEP.PAYMENT, DEFAULT_ERROR);
    console.error(err);
  };

  const buttonText = isDisabled ? (
    <span>Pay with PayPal</span>
  ) : (
    <div className="flex items-center justify-center">
      <span>Loading PayPal</span>
      <div className="ml-2">
        <Loader size="sm" />
      </div>
    </div>
  );

  if (isPending || isDisabled || !cart) {
    return (
      <Button disabled className="w-full" data-testid="checkout-button-paypal">
        {buttonText}
      </Button>
    );
  }

  return (
    <PayPalButtons
      style={{
        color: 'gold',
        shape: 'pill',
        label: 'pay',
        tagline: false,
        layout: 'horizontal',
        height: 44, // Best we can to match the height of the other buttons
      }}
      forceReRender={[cart]}
      createOrder={(_data, actions) => createOrder(_data, actions, cart)}
      onApprove={onApprove}
      onError={onError}
    />
  );
};

// https://paypal.github.io/react-paypal-js/?path=/docs/example-paypalbuttons--default
export default function PaypalCheckout({
  isDisabled,
}: Readonly<{
  isDisabled: boolean;
}>): ReactElement {
  return (
    <PayPalScriptProvider
      options={{
        'client-id': process.env.NEXT_PUBLIC_PAYPAL_CLIENT_ID ?? '',
        components: 'buttons',
        currency: 'USD',
      }}
    >
      <PaypalButtonWrapper isDisabled={isDisabled} />
    </PayPalScriptProvider>
  );
}
